"use client";

import { useLingui } from "@lingui/react/macro";
import { LocationIcon } from "@offstreetparking/design-system/icons/LocationIcon";
import { TenantIcon } from "@offstreetparking/design-system/icons/TenantIcon";
import { Text } from "@offstreetparking/design-system/typography";
import type { ColorRamp } from "@offstreetparking/domain/value/branding";
import { CommonLayout, FlowThemeProvider } from "../../host";
import { BrandingBanner } from "../BrandingBanner";
import { SummaryContent } from "../CreatePermitWizard/components/Summary/SummaryContent";
import { WysiwygView } from "../forms/Wysiwyg/WysiwygView";
import { Contact } from "../layouts/Contact";
import { ContentCard } from "../layouts/ContentCard";
type SelectionLayoutProps = {
	location?: {
		id: number;
		name: string;
	};
	selectedTenant?: {
		name: string;
		brandingFileUrl?: string | null;
	};
	branding?: {
		id: number;
		primaryColorRamp: ColorRamp;
		accentColorRamp: ColorRamp;
		companyLogoFileUrl: string | null;
		faviconFileUrl: string | null;
	};
	support?: {
		hideOffstreetSupport: boolean;
		instructions: string;
		companyName: string;
	};
	contact?: {
		email: string | null;
		phoneNumber: string | null;
		formattedPhoneNumber: string | null;
	};
	registrationInstructions?: string;
	children: React.ReactNode;
	tenantId?: number;
	onChangeLocation?: () => void;
	onChangeTenant?: () => void;
};
export const SelectionLayout = ({
	location,
	selectedTenant,
	branding,
	support,
	contact,
	registrationInstructions,
	children,
	tenantId,
	onChangeLocation,
	onChangeTenant,
}: SelectionLayoutProps) => {
	const { t } = useLingui();
	const registrationInstructionsNode = !!registrationInstructions && (
		<div className="flex w-full flex-col gap-3">
			<Text weight="bold">{t`Parking instructions`}</Text>
			<WysiwygView content={registrationInstructions} />
		</div>
	);
	const displayName = selectedTenant?.name ?? location?.name;
	const name = displayName ?? "Offstreet";
	const locationIdString = location?.id ? `${location.id}` : undefined;
	const companyName = support?.companyName ?? "Offstreet";
	const hasSummaryCards = !!location || !!selectedTenant;
	return (
		<CommonLayout
			srOnlyTitle={name}
			infoSlot={
				<div className="space-y-12">
					<h1 className="font-extrabold text-6xl">
						<span className="inline-block break-words text-primary">{name}</span>
					</h1>

					<Contact
						supportText={support?.instructions}
						hideOffstreetSupport={!!support?.hideOffstreetSupport}
						name={displayName ?? companyName}
						email={contact?.email}
						phoneNumber={contact?.phoneNumber}
						formattedPhoneNumber={contact?.formattedPhoneNumber}
						locationId={locationIdString}
						eventId={undefined}
					/>
					{registrationInstructionsNode}
				</div>
			}
			companyLogo={
				branding?.companyLogoFileUrl
					? {
							url: branding.companyLogoFileUrl,
							altText: companyName,
						}
					: undefined
			}
			eventId={undefined}
			locationId={locationIdString}
			tenantId={tenantId}
		>
			{branding && <FlowThemeProvider branding={branding} />}
			<ContentCard>
				{selectedTenant?.brandingFileUrl && <BrandingBanner brandingUrl={selectedTenant.brandingFileUrl} />}
				{hasSummaryCards && (
					<div>
						{location && (
							<div className="border-neutral-faded border-b px-4 py-3 md:px-8 md:py-4">
								<SummaryContent
									title={
										<span className="flex items-center gap-1.5">
											<LocationIcon className="h-4 w-4" />
											{t`Parking Locations`}
										</span>
									}
									action={
										onChangeLocation
											? {
													label: t`Details`,
													ariaLabel: `${t`Details`} ${t`Parking Locations`}`,
													onClick: onChangeLocation,
												}
											: undefined
									}
								>
									<Text size="sm">{location.name}</Text>
								</SummaryContent>
							</div>
						)}
						{selectedTenant && (
							<div className="border-neutral-faded border-b px-4 py-3 md:px-8 md:py-4">
								<SummaryContent
									title={
										<span className="flex items-center gap-1.5">
											<TenantIcon className="h-4 w-4" />
											{t({
												message: "Tenant",
												comment:
													"Multi-tenancy sense: a permission-bearing group under a company, not a residential renter. Keep FR locataire / ES inquilino.",
											})}
										</span>
									}
									action={
										onChangeTenant
											? {
													label: t`Change`,
													ariaLabel: t`Change Tenant`,
													onClick: onChangeTenant,
												}
											: undefined
									}
								>
									<Text size="sm">{selectedTenant.name}</Text>
								</SummaryContent>
							</div>
						)}
						<div className="border-b-2 border-b-neutral-faded" />
					</div>
				)}
				<div className="h-full w-full p-4 md:p-8 lg:h-auto">{children}</div>
				{registrationInstructionsNode && (
					<div className="border-neutral-faded border-t p-4 md:p-8 xl:hidden">
						{registrationInstructionsNode}
					</div>
				)}
				<div className="border-neutral-faded border-t p-4 md:p-8 xl:hidden">
					<Contact
						supportText={support?.instructions}
						hideOffstreetSupport={!!support?.hideOffstreetSupport}
						name={name}
						email={contact?.email}
						phoneNumber={contact?.phoneNumber}
						formattedPhoneNumber={contact?.formattedPhoneNumber}
						locationId={locationIdString}
						eventId={undefined}
					/>
				</div>
			</ContentCard>
		</CommonLayout>
	);
};
